import React from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Calendar, Hash, Loader2, MessageSquare } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useClaudeCode } from "../../context/ClaudeCodeContext";
import { displayNameFromEmail, initials } from "../UserCell";

const formatDateAt = (s: string | null | undefined) => {
  if (!s) return "—";
  try {
    const d = new Date(s);
    const date = d.toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
    const time = d.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    return `${date} at ${time}`;
  } catch {
    return s;
  }
};

interface MetaItemProps {
  label: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}

const MetaItem: React.FC<MetaItemProps> = ({ label, icon, children }) => (
  <div className="min-w-0">
    <p className="text-[10px] font-semibold tracking-[0.08em] text-gray-400 uppercase mb-1.5">
      {label}
    </p>
    <div className="flex items-center gap-1.5 text-sm text-gray-800">
      {icon}
      <span className="truncate">{children}</span>
    </div>
  </div>
);

const Pill: React.FC<{
  className?: string;
  children: React.ReactNode;
}> = ({ className = "", children }) => (
  <span
    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${className}`}
  >
    {children}
  </span>
);

const PromptsSheet: React.FC = () => {
  const {
    selectedSessionId,
    setSelectedSessionId,
    selectedSession,
    prompts,
    promptsLoading,
  } = useClaudeCode();

  const open = selectedSessionId !== null;

  return (
    <Sheet open={open} onOpenChange={(o) => !o && setSelectedSessionId(null)}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[900px] flex flex-col p-0"
      >
        <SheetHeader className="px-6 pt-6 pb-5 flex-shrink-0">
          <SheetTitle className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            <MessageSquare className="h-5 w-5 text-gray-600" />
            <span>Session History</span>
          </SheetTitle>
        </SheetHeader>

        {/* Metadata grid */}
        {selectedSession && (
          <div className="px-6 pb-5 flex-shrink-0">
            <div className="grid grid-cols-2 gap-x-8 gap-y-5">
              <MetaItem label="User">
                <span className="inline-flex items-center gap-2 min-w-0">
                  <Avatar className="h-6 w-6 flex-shrink-0">
                    {selectedSession.image ? (
                      <AvatarImage
                        src={selectedSession.image}
                        alt={selectedSession.user_name || selectedSession.user_email}
                      />
                    ) : null}
                    <AvatarFallback className="bg-[#334238] text-[#dbf566] text-[10px] font-semibold">
                      {initials(selectedSession.user_name, selectedSession.user_email)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="truncate" title={selectedSession.user_email}>
                    {selectedSession.user_name?.trim() ||
                      displayNameFromEmail(selectedSession.user_email)}
                  </span>
                </span>
              </MetaItem>
              <MetaItem
                label="Created At"
                icon={<Calendar className="h-3.5 w-3.5 text-gray-500" />}
              >
                {formatDateAt(selectedSession.first_prompt_at)}
              </MetaItem>
              <MetaItem
                label="Messages"
                icon={<Hash className="h-3.5 w-3.5 text-gray-500" />}
              >
                {selectedSession.prompt_count ?? prompts.length}
              </MetaItem>
              <MetaItem label="Source">
                <Pill className="bg-blue-50 text-blue-700">App</Pill>
              </MetaItem>
              <div className="col-span-2 min-w-0">
                <MetaItem label="App">
                  {selectedSession.app_name ? (
                    <Pill className="bg-[#fef9c3] text-[#854d0e]">
                      {selectedSession.app_name}
                    </Pill>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </MetaItem>
              </div>
            </div>
          </div>
        )}

        <div className="border-t border-gray-100 flex-shrink-0" />

        {/* Prompts */}
        <div className="flex-1 overflow-y-auto px-6 py-5 thin-scrollbar bg-white">
          {promptsLoading ? (
            <div className="flex items-center justify-center py-10 text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Loading prompts...
            </div>
          ) : prompts.length === 0 ? (
            <div className="text-center text-sm text-gray-500 py-10">
              No prompts in this session
            </div>
          ) : (
            <ul className="space-y-3">
              {prompts.map((p) => (
                <li key={p.id} className="ccd-user-bubble">
                  <div className="flex items-center justify-between gap-2 mb-1.5 text-[11px] text-[#6b7a4a]">
                    <span className="font-medium">
                      {formatDateAt(p.created_at)}
                    </span>
                    <span>{p.prompt_length} chars</span>
                  </div>
                  <p className="text-sm text-[#1f2937] leading-relaxed whitespace-pre-wrap break-words">
                    {p.prompt}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default PromptsSheet;
